import { reportException, SkywardException } from "#skyward_error";
import { mobileAccess } from "#skyward_global";
import { Binding, ModalLayer } from "#skyward_modalLayer";
import { getClientHeight, getCurrentPopupId, registerLayoutResizeCallback, registerLoadDOMCallback } from "#skyward_window";
import { CSSProperties, defineComponent, onBeforeMount, onMounted, onUnmounted, PropType, ref, Teleport } from "vue";
import { registerLayer, setFocus, unregisterLayer } from "../../skyward.ui.dialogFocusManager";
import { addScopedGlobalListener, DefaultVueProp, OptionalVueProp } from "./vueHelpers";
import debounce from "../../skyward.ui.debounce";

export default defineComponent({
    name: "ModalLayer",
    emits: {
        close: (_reason?: string) => true,
    },
    props: {
        /** 
         * A function that will be called before the modal layer closes. 
         * Return false to prevent the dialog from closing. 
         */
        // TODO make these events?
        beforeClose: OptionalVueProp<((layer: ModalLayer, reason?: string) => (void | boolean))>(Function),
        transparent: OptionalVueProp(Boolean),
        expose: DefaultVueProp<HTMLElement[]>(Array, { default: () => [] }),
        anchorTo: OptionalVueProp<HTMLElement>(),
        closeOnEscape: DefaultVueProp(true),
        containerId: DefaultVueProp(""),
        /**
         * Set preserveFocus to true to restore focus to the element that was focused before the layer was rendered. 
         * Set it to a JQueryInput value to focus a specific element.
         */
        preserveFocus: {
            type: [Boolean, Function] as PropType<boolean | ((reason: string) => boolean)>,
            required: false as false,
            default: true,
        },
        initialFocusElement: {
            required: false as false,
            default: undefined,
            validator: (val) => typeof val === "string" || typeof val === "undefined" || val instanceof $ || val instanceof HTMLElement,
        },
        /**
         * ARIA Role of the Modal Layer Contents
         */
        role: DefaultVueProp("dialog"),
        ariaLabel: OptionalVueProp(String),
        bindings: DefaultVueProp<Binding[]>(Array, { default: () => [] })
    },
    setup(props, context)
    {
        const layerId = document.body.querySelectorAll(".modalLayerContainer").length;

        const containerRef = ref<HTMLElement>();
        const wrapperRef = ref<HTMLElement>();
        const glassRef = ref<HTMLElement>();
        let originalActiveElement: HTMLElement;
        const anchorStyle = ref<CSSProperties>({});
        let layer: ModalLayer;

        function updateAnchorStyle()
        {
            anchorStyle.value = props.anchorTo
                ? getAnchorStyle(props.anchorTo)
                : {};
        }

        function preventScrollingOnExposedElements(e: WheelEvent)
        {
            e.preventDefault();
            return false;
        }

        onBeforeMount(() =>
        {
            originalActiveElement = document.activeElement as HTMLElement;
            updateAnchorStyle();
            addScopedGlobalListener(window, "resize", debouncedUpdate);
            addScopedGlobalListener(window, "orientationchange", debouncedUpdate);
        });

        const debouncedUpdate = debounce(() =>
        {
            requestAnimationFrame(updateAnchorStyle);
        }, 120);

        onMounted(() =>
        {
            layer =
            {
                get container() { return $(containerRef.value!); },
                get contents() { return $(wrapperRef.value!) },
                get glass() { return $(glassRef.value!) },
                setupContent: () => { },
                addContent: () => { },
                show: showLayer,
                uniqueId: layerId,
                onscreen: true,
                options: props,
                originalActiveElement: originalActiveElement,
                closing: false,
                close,
                _bindingPoints: [],
                _exposedByUnderlyingLayer: [],
                _exposedElements: [],
            };

            const exposedClassName = "exposedToModalLayer_" + layer.uniqueId;
            props.expose?.forEach((element) =>
            {
                layer._exposedElements.push(
                {
                    element: element,
                    previousOpacity: element.style.opacity,
                    previousZIndex: element.style.zIndex,
                    previousPosition: element.style.position
                });
                element.style.zIndex = '1';
                element.style.position = 'relative';
                element.classList.add(exposedClassName);
                element.addEventListener('wheel', preventScrollingOnExposedElements);
            });

            if (!mobileAccess)
            {
                // Register the Modal Layer with the Dialog Focus Manager
                registerLayer(layer);
            }

            if (props.closeOnEscape)
            {
                addScopedGlobalListener(document.body, "keydown", (event) =>
                {
                    if (event.key === "Escape")
                    {
                        const currentPopupId = getCurrentPopupId();

                        if (layer.uniqueId === currentPopupId)
                        {
                            close('escape');
                            event.stopImmediatePropagation();
                        }
                    }
                });
            }

            onUnmounted(() =>
            {
                props.expose?.forEach((element) =>
                {
                    const originalElement = layer._exposedElements.find(style => style.element === element)!;
                    if (originalElement.previousZIndex && originalElement.previousZIndex.length > 0)
                    {
                        element.style.zIndex = originalElement.previousZIndex;
                    }
                    else
                    {
                        element.style.removeProperty('z-index');
                    }

                    if (originalElement.previousPosition && originalElement.previousPosition.length > 0)
                    {
                        element.style.position = originalElement.previousPosition;
                    }
                    else
                    {
                        element.style.removeProperty('position');
                    }

                    element.classList.remove("exposedToModalLayer_" + layer.uniqueId, "currentlyExposed");
                });

                if (!mobileAccess)
                {
                    unregisterLayer(layer, true);
                }
            });
        });

        return () =>
        {
            return <Teleport to="body" >
                <div
                    class="modalLayerContainer"
                    data-layer-id={layerId}
                    id={props.containerId}
                    ref={containerRef}>
                    <div class={{ glass: true, transparent: props.transparent }} id={`modalLayerGlass${layerId}`} ref={glassRef} onClick={() => close("glassClick")} />
                    <div style={anchorStyle.value} ref={wrapperRef} aria-modal="true" role={props.role} aria-label={props.ariaLabel}>
                        {context.slots.default?.()}
                    </div>
                </div>
            </Teleport>;
        };

        function showLayer()
        {
            if (!layer)
            {
                reportException(new SkywardException("layer is undefined within show function.", `layerId: ${layerId}`));
                return;
            }
            if (layer.options.anchored)
            {
                layer.contents.addClass('anchored');
                if (layer.options.tailessAnchored)
                {
                    layer.contents.addClass('noAnchoredTail');
                }
            }
            layer.contents.addClass('columnLayoutWrapper--modalLayer');
            layer.container.show();
            if (layer.options.loadDOMCallback && typeof layer.options.loadDOMCallback === 'function')
            {
                registerLoadDOMCallback(layer.options.loadDOMCallback);
            }
            if (layer.options.resizeCallback && typeof layer.options.resizeCallback === 'function')
            {
                registerLayoutResizeCallback(layer.options.resizeCallback);
            }
            if (!mobileAccess)
            {
                setFocus(layer);
            }
        }

        function close(reason?: string)
        {
            if (layer.closing)
            {
                return true;
            }

            if (props.beforeClose?.(layer, reason) === false)
            {
                return false;
            }

            layer.closing = true;
            context.emit("close", reason);
            return true;
        }
    }
});

function getAnchorStyle(anchorTo: HTMLElement)
{
    const rect = anchorTo.getBoundingClientRect();

    // Determine if there is more room above or below the anchor element to render the modal
    const showBelow = moreSpaceBelowThanAbove(anchorTo);
    const style: CSSProperties = {};
    style.position = "absolute";

    // Calculate the position to anchor the modal layer
    if (showBelow)
    {
        const bottom = rect.bottom + window.scrollY;
        const left = rect.left + window.scrollX;

        style.top = `${bottom}px`;
        style.left = `${left}px`;
    }
    else 
    {
        const clientHeight = getClientHeight();
        const top = clientHeight - (rect.top + window.scrollY);
        const left = rect.left + window.scrollX;
        
        style.bottom = `${top}px`;
        style.left = `${left}px`;
    }
    return style;
}

function moreSpaceBelowThanAbove(anchorTo: HTMLElement)
{
    const rect = anchorTo.getBoundingClientRect();
    const spaceBelowAnchorTo = getClientHeight() - rect.bottom;
    const spaceAboveAnchorTo = rect.top;

    return spaceBelowAnchorTo >= spaceAboveAnchorTo;
}
